import {isFeatureEnabled} from 'feature-flags';
import {Space, Text} from 'platform/foundation';

import {useCallback, useEffect} from 'react';
import {FieldValues, useFormState} from 'react-hook-form';
import {BlockerFunction, useBlocker, useInRouterContext, Blocker} from 'react-router-dom';

import {equals, isNotEmpty, not} from 'ramda';

import {Button} from '../../Button/Button';
import {ButtonGroup} from '../../ButtonGroup/ButtonGroup';
import {Dialog} from '../../Dialog/Dialog';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';
import {useFormContext} from '../hooks/useFormContext';

interface UnsavedChangesBlockerProps {
  isDisabled: boolean;
  shouldNavigateWithoutBlockerRef: React.MutableRefObject<boolean>;
}

const UNSAVED_FORMS_FF_V2 = 'core_unsaved_forms_v2';

const UNBLOCKED_STATE: Blocker = {
  state: 'unblocked',
  reset: undefined,
  proceed: undefined,
  location: undefined,
};

export function UnsavedChangesBlocker<TFieldValues extends FieldValues>(
  props: UnsavedChangesBlockerProps
) {
  const t = useTranslationContext();
  const formApi = useFormContext<TFieldValues>();
  const isWithinRouterContext = useInRouterContext();
  const isDisabled = props.isDisabled || !isWithinRouterContext;

  const {dirtyFields, isSubmitting} = useFormState({control: formApi.control});

  const isDirty = isNotEmpty(dirtyFields);

  const shouldBlock = useCallback<BlockerFunction>(
    ({currentLocation, nextLocation}) =>
      isDirty &&
      isFeatureEnabled(UNSAVED_FORMS_FF_V2) &&
      not(props.isDisabled) &&
      not(props.shouldNavigateWithoutBlockerRef.current) &&
      not(isSubmitting) &&
      not(equals(currentLocation, nextLocation)),
    [isDirty, isSubmitting, props.isDisabled, props.shouldNavigateWithoutBlockerRef]
  );

  const getUnblockedState = () => UNBLOCKED_STATE;
  const useAppropriateBlocker = isDisabled ? getUnblockedState : useBlocker;
  const blocker = useAppropriateBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked' && not(isDirty)) {
      blocker.reset?.();
    }
  }, [blocker, isDirty]);

  const handleLeave = () => {
    formApi.reset();
    blocker.proceed?.();
  };

  const handleStayOnPage = () => {
    blocker.reset?.();
  };

  return (
    <Dialog
      isOpen={blocker.state === 'blocked'}
      onClose={handleStayOnPage}
      size="small"
      title={t('general.notifications.unsavedChanges.title')}
      data-testid="formSaver-dialog"
    >
      <Text size="small">{t('general.notifications.unsavedChanges.description')}</Text>

      <Space vertical={4} />

      <ButtonGroup align="right">
        <Button
          variant="secondary"
          title={t('general.labels.leavePage')}
          onClick={handleLeave}
          data-testid="formSaverModal-button-yes"
        />
        <Button
          title={t('general.labels.stayOnPage')}
          onClick={handleStayOnPage}
          data-testid="formSaverModal-button-no"
        />
      </ButtonGroup>
    </Dialog>
  );
}
